import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { theme } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { ChevronLeft, ChevronRight } from '@hhgtech/icons/arrow'
import { Carousel, Embla } from '@mantine/carousel'
import { Box, clsx, Flex, Progress, rem } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import Link, { LinkProps } from 'next/link'

import { getHelloSitesUrl } from 'api/utils/paths'
import AppIcon from 'components/atoms/icons/AppIcon'
import Phone from 'components/atoms/icons/Phone'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'

export type RelatedToolItem = {
  id: number
  title: string
  icon: string
  link: string
  subtitle: string
  template: string
  is_subot: boolean
  is_mobile_app_tool: boolean
  enable_ad: boolean
  lang: string
}
type RelatedToolCardProps = {
  item: RelatedToolItem
  showIconPhone?: boolean
} & Partial<LinkProps>
const RelatedToolCard: FC<RelatedToolCardProps> = (props) => {
  const { item, showIconPhone, ...restProps } = props
  const { link, icon, title } = item
  const isMobileApp = !!item?.is_mobile_app_tool

  return (
    <Link
      href={link}
      target="_blank"
      className={clsx(
        'relatedTool--card flex items-center gap-4 p-3 pr-4',
        `relative bg-white rounded-lg`,
        `md:flex-col md:items-start md:min-h-[128px] md:h-full`,
        '[&+&]:max-md:mt-2',
      )}
      {...restProps}
    >
      <div
        className={clsx(
          'relatedTool--icon relative w-10 h-10 bg-neutral50 rounded-[50%] overflow-hidden p-1',
          'max-md:flex max-md:items-center',
        )}
      >
        <img
          loading="lazy"
          src={icon}
          alt={title}
          className={clsx('relatedTool--icon_img z-[2] relative')}
        />
      </div>
      <div
        className={clsx(
          'relatedTool--card_content',
          'md:min-h-0 md:flex-1 md:flex md:items-center md:flex-wrap',
        )}
      >
        <Text
          as="span"
          size="p2"
          color={theme.colors.gray800}
          className="m-0 w-full block"
        >
          {title}
        </Text>
        {isMobileApp && (
          <Flex gap={rem(6)} align="center" py={rem(5)}>
            <AppIcon className="text-base leading-none w-[1em] h-[1em]" />
            <Text as="span" size="n1" color={theme.colors.blue800}>
              Hello Bacsi app
            </Text>
          </Flex>
        )}
      </div>

      {isMobileApp && showIconPhone && (
        <Flex
          w={rem(32)}
          h={rem(32)}
          align="center"
          justify="center"
          className="absolute top-0 right-0 p-[6px] text-xl leading-none"
        >
          <Phone className="w-[1em] h-[1em]" />
        </Flex>
      )}
    </Link>
  )
}

const getRelatedTracking = (item: RelatedToolItem, currentDomain: string) => {
  const { link } = item
  const dataEventLabel = link.includes('https://')
    ? link
    : `${currentDomain}${basePath}${link}`
  return {
    'data-event-category': 'Health Tool',
    'data-event-action': 'Due Date - Helpful Tips - Explore More Health Tool',
    'data-event-label': dataEventLabel,
  }
}

export type RelatedToolsProps = {
  id?: string
  title?: string
  tools?: RelatedToolItem[]
  className?: string
  style?: CSSProperties
}
export const RelatedTools: FC<RelatedToolsProps> = (props) => {
  const { id, title, tools, className, style } = props
  const isMobile = useIsMobile()
  const slideScrollAuto = useMediaQuery(`(max-width: 1200px)`)
  const { locale } = useRouterExtend()

  const [embla, setEmbla] = useState<Embla | null>(null)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [isPrev, setIsPrev] = useState(false)
  const [isNext, setIsNext] = useState(false)

  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])
  const carouselChildren = useMemo(() => {
    if (!tools || !tools.length) {
      return []
    }

    if (!isMobile) {
      return tools.map((tool, index) => {
        return (
          <Carousel.Slide key={`${index}-${tool.id}`}>
            <RelatedToolCard
              item={tool}
              showIconPhone={!isMobile}
              {...getRelatedTracking(tool, currentDomain)}
            />
          </Carousel.Slide>
        )
      })
    }

    const toolsArranged = []
    let index = 0
    while (index < tools.length) {
      toolsArranged.push(tools.slice(index, index + 3))
      index += 3
    }

    return toolsArranged.map((toolArranged, index) => {
      return (
        <Carousel.Slide key={`${index}`}>
          {toolArranged.map((tool) => (
            <RelatedToolCard
              key={`${index}-${tool.id}`}
              item={tool}
              showIconPhone={!isMobile}
              {...getRelatedTracking(tool, currentDomain)}
            />
          ))}
        </Carousel.Slide>
      )
    })
  }, [tools, currentDomain, isMobile])
  const isShowNavigation = tools && tools.length > 3

  const handleScroll = useCallback(() => {
    if (!embla) return

    const progress = Math.max(0, Math.min(1, embla.scrollProgress()))
    const firstProgress = 1 / embla.slidesInView().length

    setScrollProgress(
      (progress < firstProgress ? firstProgress : progress) * 100,
    )
    setIsPrev(embla.canScrollPrev())
    setIsNext(embla.canScrollNext())
  }, [embla, setScrollProgress])

  useEffect(() => {
    if (!embla) {
      return
    }
    handleScroll()
    embla.on('scroll', handleScroll)
    return () => {
      embla.off('scroll', handleScroll)
    }
  }, [embla])

  if (!carouselChildren.length) {
    return null
  }

  return (
    <Box id={id} className={clsx(className, 'max-md:px-4')} style={style}>
      <Box className="max-md:bg-neutral50 max-md:p-4 max-md:rounded-lg">
        <Text
          as="h3"
          size="s1"
          color={theme.colors.gray800}
          weight="semiBold"
          className="mb-4 md:mb-2"
        >
          {title}
        </Text>
        <Box className="relative max-md:-mx-4">
          <Carousel
            dragFree
            align="start"
            slideGap={rem(8)}
            slideSize={`calc(100% / 3 - ${rem(8)} / 2)`}
            slidesToScroll={slideScrollAuto ? 'auto' : 3}
            breakpoints={[
              { maxWidth: 'md', slideSize: `calc(100% - ${rem(8)})` },
              { minWidth: 'md', slideSize: `calc(50% - ${rem(8)} / 2)` },
              {
                minWidth: 'lg',
                slideSize: `calc(100% / 3 - ${rem(8)} / 2)`,
              },
            ]}
            withControls={false}
            getEmblaApi={setEmbla}
            classNames={{
              viewport: clsx('max-md:px-4'),
            }}
          >
            {carouselChildren}
          </Carousel>
          {!isMobile && isShowNavigation && (
            <>
              <Flex
                w={32}
                h={32}
                align="center"
                justify="center"
                bg={theme.colors.pink100}
                sx={{
                  borderRadius: '100%',
                  cursor: 'pointer',
                  transform: 'translate(-50%, -50%)',
                }}
                onClick={() => embla?.scrollPrev()}
                className={clsx(
                  'absolute z-[1] top-1/2 left-0 opacity-0 transition-all ease-linear duration-200 pointer-events-none',
                  { 'opacity-100 pointer-events-auto': isPrev },
                )}
              >
                <ChevronLeft size={20} color={theme.colors.pink800} />
              </Flex>
              <Flex
                w={32}
                h={32}
                align="center"
                justify="center"
                bg={theme.colors.pink100}
                sx={{
                  borderRadius: '100%',
                  cursor: 'pointer',
                  transform: 'translate(50%, -50%)',
                }}
                onClick={() => embla?.scrollNext()}
                className={clsx(
                  'absolute z-[1] top-1/2 right-0 opacity-0 transition-all ease-linear duration-200 pointer-events-none',
                  { 'opacity-100 pointer-events-auto': isNext },
                )}
              >
                <ChevronRight size={20} color={theme.colors.pink800} />
              </Flex>
            </>
          )}
          {isMobile && isShowNavigation && (
            <Progress
              value={scrollProgress}
              styles={{
                bar: {
                  transitionDuration: '0ms',
                  backgroundColor: theme.colors.neutral300,
                  borderRadius: '0 !important',
                },
                root: {
                  height: rem(4),
                  marginTop: `${rem(16)} !important`,
                  maxWidth: `calc(100% - ${rem(32)})`,
                  backgroundColor: theme.colors.neutral100,
                  borderRadius: '0 !important',
                },
              }}
              size="sm"
              mt="xl"
              mx="auto"
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
